type EnvRecord = Record<string, string | undefined>;

const PROD_ORIGIN = "https://ozknews.com";
const PREVIEW_FALLBACK = "https://ozknews.pages.dev";
const DEFAULT_KAKAO_PATH = "/api/auth/kakao/callback";

const ABSOLUTE_URL_PATTERN = /^[a-zA-Z][a-zA-Z\d+\-.]*:/;

export function isPreviewHost(input: string): boolean {
  const origin = normalizeOrigin(input);
  if (!origin) return false;
  try {
    const { hostname } = new URL(origin);
    return (
      hostname.endsWith(".pages.dev") ||
      hostname === "ozknews.pages.dev" ||
      hostname === "ozknews-v1.pages.dev"
    );
  } catch {
    return false;
  }
}

export function resolveSiteUrl(env?: EnvRecord): string {
  const runtimeOrigin = getRuntimeOrigin();
  if (runtimeOrigin) return runtimeOrigin;

  const envSources = collectEnvSources(env);

  const previewCandidate = pickEnvValue(envSources, [
    "CF_PAGES_URL",
    "PAGES_URL",
    "DEPLOY_PRIME_URL",
  ]);
  const normalizedPreview = normalizeOrigin(previewCandidate || "");
  if (normalizedPreview && isPreviewHost(normalizedPreview)) {
    return normalizedPreview;
  }

  const siteCandidate = pickEnvValue(envSources, [
    "SITE_URL",
    "APP_BASE_URL",
    "VITE_SITE_URL",
    "URL",
  ]);
  const normalizedSite = normalizeOrigin(siteCandidate || "");
  if (normalizedSite) return normalizedSite;

  if (normalizedPreview) return normalizedPreview;

  return isPreviewHost(getProcessEnvValue("CF_PAGES_URL"))
    ? PREVIEW_FALLBACK
    : PROD_ORIGIN;
}

export function resolveFrontOrigin(env?: EnvRecord): string {
  const envSources = collectEnvSources(env);
  const frontCandidate = pickEnvValue(envSources, [
    "FRONT_ORIGIN",
    "VITE_SITE_URL",
  ]);
  const normalizedFront = normalizeOrigin(frontCandidate || "");
  if (normalizedFront) return normalizedFront;
  return resolveSiteUrl(env);
}

export function resolveOAuthRedirectOrigin(env?: EnvRecord): string {
  const envSources = collectEnvSources(env);
  const redirectCandidate = pickEnvValue(envSources, ["OAUTH_REDIRECT_ORIGIN"]);
  const normalized = normalizeOrigin(redirectCandidate || "");
  if (normalized) return normalized;
  return resolveSiteUrl(env);
}

export function buildKakaoRedirectUri(
  env?: EnvRecord,
  path = DEFAULT_KAKAO_PATH
): string {
  const envSources = collectEnvSources(env);
  const explicit = pickEnvValue(envSources, ["KAKAO_REDIRECT_URI"]);
  const normalizedExplicit = normalizeUrl(explicit || "");
  if (normalizedExplicit) return normalizedExplicit;
  const origin = resolveOAuthRedirectOrigin(env);
  return buildAbsoluteUrl(origin, path);
}

export function buildAbsoluteUrl(
  base: string | undefined,
  target: string
): string {
  if (typeof target === "string" && ABSOLUTE_URL_PATTERN.test(target)) {
    return normalizeUrl(target) || target;
  }

  const origin = normalizeOrigin(base || "") || resolveSiteUrl();
  const relative = target.startsWith("/") ? target : `/${target}`;
  try {
    return new URL(relative, `${origin}/`).toString();
  } catch {
    return `${origin}${relative}`;
  }
}

export function normalizeUrl(input: string): string | undefined {
  const trimmed = `${input || ""}`.trim();
  if (!trimmed) return undefined;

  const hasProtocol = ABSOLUTE_URL_PATTERN.test(trimmed);
  const scheme = hasProtocol
    ? ""
    : `${isLikelyLocal(trimmed) ? "http" : "https"}://`;

  try {
    const url = new URL(`${scheme}${trimmed}`);
    url.hash = "";
    return url.toString();
  } catch {
    return undefined;
  }
}

export function normalizeOrigin(input: string): string | undefined {
  const normalized = normalizeUrl(input);
  if (!normalized) return undefined;
  try {
    const url = new URL(normalized);
    return `${url.protocol}//${url.host}`;
  } catch {
    return undefined;
  }
}

function getRuntimeOrigin(): string | undefined {
  const runtimeLocation = globalThis.location;
  if (runtimeLocation?.origin) {
    const normalized = normalizeOrigin(runtimeLocation.origin);
    if (normalized) return normalized;
  }

  if (typeof globalThis !== "undefined") {
    const maybeLocation = (globalThis as any).location;
    if (maybeLocation?.origin) {
      const normalized = normalizeOrigin(String(maybeLocation.origin));
      if (normalized) return normalized;
    }
  }

  return undefined;
}

function collectEnvSources(env?: EnvRecord): EnvRecord[] {
  const sources: EnvRecord[] = [];
  if (env) sources.push(env);

  const runtimeEnv = getRuntimeEnv();
  if (runtimeEnv) sources.push(runtimeEnv);

  const importMetaEnv = getImportMetaEnv();
  if (importMetaEnv) sources.push(importMetaEnv);

  const processEnv = getProcessEnv();
  if (processEnv) sources.push(processEnv);

  return sources;
}

function pickEnvValue(envs: EnvRecord[], keys: string[]): string | undefined {
  for (const env of envs) {
    if (!env) continue;
    for (const key of keys) {
      const value = env[key];
      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }
  }
  return undefined;
}

function getRuntimeEnv(): EnvRecord | undefined {
  if (typeof globalThis !== "undefined") {
    const runtimeEnv = (globalThis as any).__OZK_ENV__;
    if (runtimeEnv && typeof runtimeEnv === "object") {
      return runtimeEnv as EnvRecord;
    }
  }

  if (typeof globalThis !== "undefined") {
    const globalEnv = (globalThis as any).__OZK_ENV__;
    if (globalEnv && typeof globalEnv === "object") {
      return globalEnv as EnvRecord;
    }
  }

  return undefined;
}

function getImportMetaEnv(): EnvRecord | undefined {
  try {
    const meta = (import.meta as any)?.env;
    if (meta && typeof meta === "object") {
      return meta as EnvRecord;
    }
  } catch {
    /* no-op */
  }
  return undefined;
}

function getProcessEnv(): EnvRecord | undefined {
  if (typeof process !== "undefined" && process?.env) {
    return process.env as EnvRecord;
  }
  return undefined;
}

function getProcessEnvValue(key: string): string {
  if (typeof process !== "undefined" && process?.env?.[key]) {
    return String(process.env[key]);
  }
  return "";
}

function isLikelyLocal(host: string): boolean {
  return (
    host.startsWith("localhost") ||
    host.startsWith("127.") ||
    host.startsWith("0.") ||
    host.startsWith("::1") ||
    host.includes(".local") ||
    /^[\d.:]+$/.test(host)
  );
}
